import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { DEFAULT_LOCALE } from "../../../../app/i18n";
import { commissionBatchApi } from "../../../../modules/commissions/batch/api";
import {
  CalculateBatchPayments,
  ChangeCommissionsBatchStepRequest,
  CommissionsBatch,
  CommissionsBatchFilterPageRequest,
  CommissionsBatchOutputFilterPageRequest,
  CommissionsFilterPageRequest,
  CreateCommissionsBatch,
  CreateCommissionsUnit,
  CreateManuallyAddedCommission,
  DeleteCommissionsUnitRequest,
  DownloadBatchOutputsAsZip,
  ExcludeSpecialCommission,
  GenerateBatchPaymentOrder,
  ManualBailAccountMovementsFilterPageRequest,
  PostponeCommissions,
  RecalculateCommissionsForContractRequest,
  UpdateCalculatedCommissionList,
  UpdateCommission,
  UpdateCommissionsBatch,
  UpdateCommissionsBatchFinishedDate,
  UpdateCommissionsUnit,
} from "../../../../modules/commissions/batch/types";
import { ManualDebtAccountMovementsFilterPageRequest } from "../../../../modules/commissions/debtaccount/types";
import { SpecialCommissionsFilterPageRequest } from "../../../../modules/commissions/special/types";
import { EnumerationType } from "../../../../modules/enumerations/enums";
import { InstitutionType } from "../../../../modules/institution/enums";
import { UUID } from "../../../../typings/global";
import { ExportFileType } from "../../../enums";
import {
  EntityObject,
  OptimisticLockVersion,
  OptimisticLockVersionList,
  ThreeLevelEntityObject,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../types";
import { invalidateEnumeration } from "../enumerations";
import { COMMISSIONS_BATCH_QUERY_KEYS, COMMISSIONS_OUTPUT_QUERY_KEYS } from "./queryKeys";

/**
 * Step changes and input imports run asynchronously on the server, so the detail is polled until they finish.
 */
const BATCH_IN_PROGRESS_REFETCH_INTERVAL = 4000;

const sortCommissionsUnitsByInstitution = (batch: CommissionsBatch): CommissionsBatch => {
  const institutionTypeOrder = Object.values(InstitutionType);

  return {
    ...batch,
    commissionsUnits: [...batch.commissionsUnits].sort(
      (unit1, unit2) =>
        institutionTypeOrder.indexOf(unit1.institution.type) - institutionTypeOrder.indexOf(unit2.institution.type) ||
        unit1.institution.name.localeCompare(unit2.institution.name, DEFAULT_LOCALE, { sensitivity: "accent" }),
    ),
  };
};

export const useFilterCommissionsBatches = (filter: CommissionsBatchFilterPageRequest) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatches(filter),
    queryFn: async () => {
      const { data } = await commissionBatchApi.filterCommissionsBatches(filter);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetCommissionsBatch = (id: UUID) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(id),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await commissionBatchApi.getCommissionsBatch({ id });

      return data;
    },
    select: sortCommissionsUnitsByInstitution,
    refetchInterval: (query) => {
      const batch = query.state.data;

      return batch?.stepChangeInProgress || batch?.inputs.some((input) => input.importInProgress)
        ? BATCH_IN_PROGRESS_REFETCH_INTERVAL
        : false;
    },
  });
};

export const useGetCommissionsBatchNamePrefix = (enabled?: boolean) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatchNamePrefix(),
    queryFn: async () => {
      const { data } = await commissionBatchApi.getCommissionsBatchNamePrefix();

      return data;
    },
    enabled,
    staleTime: 0,
  });
};

export const useUpdateCommissionsBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.updateCommissionsBatch(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateCommissionsBatch>) => {
      const { data } = await commissionBatchApi.updateCommissionsBatch(payload);

      return data;
    },
    onSuccess: (data) => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_BATCHES);
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(data.id) });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatches() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useChangeCommissionsBatchStep = ({ globalSpinner = true } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.changeCommissionsBatchStep(),
    meta: { globalSpinner },
    mutationFn: async (payload: EntityObject<ChangeCommissionsBatchStepRequest>) => {
      const { data } = await commissionBatchApi.changeCommissionsBatchStep(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(data.id) });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatches() });
    },
  });
};

export const useDownloadCommissionsBatchOutputsAsZip = () => {
  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.downloadCommissionsBatchOutputsAsZip(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<DownloadBatchOutputsAsZip>) => {
      const response = await commissionBatchApi.downloadCommissionsBatchOutputsAsZip(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useCreateCommissionsUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.createCommissionsUnit(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateCommissionsUnit>) => {
      const { data } = await commissionBatchApi.createCommissionsUnit(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommissionsUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.updateCommissionsUnit(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateCommissionsUnit>) => {
      const { data } = await commissionBatchApi.updateCommissionsUnit(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id1) });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommissionsUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.deleteCommissionsUnit(),
    mutationFn: (payload: EntityObject<DeleteCommissionsUnitRequest>) => {
      return commissionBatchApi.deleteCommissionsUnit(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
      Notification.itemDeletedNotification();
    },
  });
};

export const useUploadCommissionsBatchInput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.uploadCommissionsBatchInput(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await commissionBatchApi.uploadCommissionsBatchInput(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
    },
  });
};

export const useReplaceCommissionsBatchInput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.replaceCommissionsBatchInput(),
    mutationFn: async (payload: TwoLevelEntityObject<FormData>) => {
      const { data } = await commissionBatchApi.replaceCommissionsBatchInput(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id1) });
    },
  });
};

export const useDeleteCommissionsBatchInput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.deleteCommissionsBatchInput(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return commissionBatchApi.deleteCommissionsBatchInput(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id1) });
    },
  });
};

export const useFilterManualBailAccountMovements = (
  payload: EntityObject<ManualBailAccountMovementsFilterPageRequest>,
) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterManualBailAccountMovements(payload),
    queryFn: async () => {
      const { data } = await commissionBatchApi.filterManualBailAccountsMovements(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useAssociateBailAccountMovementToBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.associateBailAccountMovementToBatch(),
    mutationFn: async (payload: TwoLevelEntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionBatchApi.associateBailAccountMovementToBatch(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterManualBailAccountMovements() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDisassociateBailAccountMovementFromBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.disassociateBailAccountMovementFromBatch(),
    mutationFn: async (payload: TwoLevelEntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionBatchApi.disassociateBailAccountMovementFromBatch(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterManualBailAccountMovements() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useFilterSpecialCommissions = (payload: TwoLevelEntityObject<SpecialCommissionsFilterPageRequest>) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterSpecialCommissions(payload),
    queryFn: async () => {
      const { data } = await commissionBatchApi.filterSpecialCommissions(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useExcludeSpecialCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.excludeSpecialCommission(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: ThreeLevelEntityObject<ExcludeSpecialCommission>) => {
      const { data } = await commissionBatchApi.excludeSpecialCommission(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterSpecialCommissions() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useIncludeSpecialCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.includeSpecialCommission(),
    mutationFn: async (payload: ThreeLevelEntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionBatchApi.includeSpecialCommission(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterSpecialCommissions() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDownloadCommissionsBatchOutputsExport = () => {
  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.downloadCommissionsBatchOutputsExport(),
    mutationFn: async (id: UUID) => {
      const response = await commissionBatchApi.downloadCommissionsBatchOutputsExport({ id });
      openBlobFile(response);

      return response.data;
    },
  });
};

export const useDownloadCommissionsBatchInputsExport = () => {
  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.downloadCommissionsBatchInputsExport(),
    mutationFn: async (payload: EntityObject<{ exportFileType: ExportFileType }>) => {
      const response = await commissionBatchApi.downloadCommissionsBatchInputsExport(payload);
      openBlobFile(response);

      return response.data;
    },
  });
};

export const useDownloadCommissionsBatchInactiveAgentsExport = () => {
  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.downloadCommissionsBatchInactiveAgentsExport(),
    mutationFn: async (payload: EntityObject<{ exportFileType: ExportFileType }>) => {
      const response = await commissionBatchApi.downloadCommissionsBatchInactiveAgentsExport(payload);
      openBlobFile(response);

      return response.data;
    },
  });
};

export const useUpdateCommissionsBatchFinishedDate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.updateCommissionsBatchFinishedDate(),
    mutationFn: async (payload: EntityObject<UpdateCommissionsBatchFinishedDate>) => {
      const { data } = await commissionBatchApi.updateCommissionsBatchFinishedDate(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatches() });
    },
  });
};

export const useImportSpecialCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.importSpecialCommissions(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const response = await commissionBatchApi.importSpecialCommissions(payload);
      openBlobFile(response);

      return response.data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatchOutputs(),
      });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
    },
  });
};

export const useCreateCommissionsBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.createCommissionsBatch(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateCommissionsBatch) => {
      const { data } = await commissionBatchApi.createCommissionsBatch(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_BATCHES);
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatches() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useDeleteCommissionsBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.deleteCommissionsBatch(),
    mutationFn: (id: UUID) => {
      return commissionBatchApi.deleteCommissionsBatch({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_BATCHES);
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatches() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useFilterManualDebtAccountMovements = (
  payload: EntityObject<ManualDebtAccountMovementsFilterPageRequest>,
) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterManualDebtAccountMovements(payload),
    queryFn: async () => {
      const { data } = await commissionBatchApi.filterManualDebtAccountMovements(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useAssociateManualDebtAccountMovementToBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.associateManualDebtAccountMovementToBatch(),
    mutationFn: async (payload: TwoLevelEntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionBatchApi.associateManualDebtAccountMovementToBatch(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterManualDebtAccountMovements() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDisassociateManualDebtAccountMovementFromBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.disassociateManualDebtAccountMovementFromBatch(),
    mutationFn: async (payload: TwoLevelEntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionBatchApi.disassociateManualDebtAccountMovementFromBatch(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterManualDebtAccountMovements() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useFilterCommissionsBatchOutputs = (payload: EntityObject<CommissionsBatchOutputFilterPageRequest>) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatchOutputs(payload),
    queryFn: async () => {
      const { data } = await commissionBatchApi.filterCommissionsBatchOutputs(payload);

      return data;
    },
    staleTime: 0,
  });
};

export const useCalculateCommissionsBatchPayments = (
  payload: EntityObject<CalculateBatchPayments>,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.calculateCommissionsBatchPayments(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await commissionBatchApi.calculateCommissionsBatchPayments(payload);

      return data;
    },
    enabled,
    staleTime: 0,
  });
};

export const useGenerateCommissionsBatchPaymentOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.generateCommissionsBatchPaymentOrder(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<GenerateBatchPaymentOrder>) => {
      const response = await commissionBatchApi.generateCommissionsBatchPaymentOrder(payload);
      openBlobFile(response, true);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatchOutputs(),
      });
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterAgentCommissionsOutputs(),
      });
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterCommissionsOutputs(),
      });
    },
  });
};

export const useFilterCommissions = (payload: EntityObject<CommissionsFilterPageRequest>, enabled?: boolean) => {
  return useQuery({
    queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions(payload),
    queryFn: async () => {
      const { data } = await commissionBatchApi.filterCommissions(payload);

      return data;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useCreateCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.createCommission(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateManuallyAddedCommission>) => {
      const { data } = await commissionBatchApi.createCommission(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
      // The batch header sums up its commissions, so it goes stale whenever one of them changes.
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.updateCommission(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateCommission>) => {
      const { data } = await commissionBatchApi.updateCommission(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id1) });
      Notification.itemUpdatedNotification();
    },
  });
};

export const usePostponeCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.postponeCommission(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<PostponeCommissions>) => {
      const { data } = await commissionBatchApi.postponeCommission(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
      Notification.itemUpdatedNotification(data.filter((commission) => commission.postponed).length);
    },
  });
};

export const useTryToIncludeCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.tryToIncludeCommission(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersionList>) => {
      const { data } = await commissionBatchApi.tryToIncludeCommission(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
      Notification.itemUpdatedNotification(data.filter((commission) => !commission.postponed).length);
    },
  });
};

export const useRecalculateCommissionsForContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.recalculateCommissionsForContract(),
    mutationFn: async (payload: EntityObject<RecalculateCommissionsForContractRequest>) => {
      const { data } = await commissionBatchApi.recalculateCommissionsForContract(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id) });
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("commissions.batch.helpers.recalculationResult", { count: data.length }),
      });
    },
  });
};

export const useUpdateCalculatedCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.updateCalculatedCommissions(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateCalculatedCommissionList>) => {
      const { data } = await commissionBatchApi.updateCalculatedCommissions(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id1) });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_BATCH_QUERY_KEYS.deleteCommission(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return commissionBatchApi.deleteCommission(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.getCommissionsBatch(mutatedPayload.id1) });
      Notification.itemDeletedNotification();
    },
  });
};
